/**
 * The Claude token pool's shared contract: a token's durable fingerprint, the payloads of
 * the host's `v3.host.tokens.*` rpcs, and the parser that tells a usage-limit exit from any
 * other failure. The rpc group itself lives in `@fm/api` (`rpc/v3/v3-host-tokens.rpc.ts`),
 * because it carries the api's auth middleware.
 *
 * **How a token is used.** The api owns the pool (`CLAUDE_CODE_OAUTH_TOKENS`). The loop host
 * leases a token per agent (`v3.host.tokens.lease`, by index into that list); when an agent
 * exits on a usage limit ({@link usageLimitOf}), the host reports the token by its
 * {@link tokenFingerprint} (`v3.host.tokens.exhausted`), so the mark survives list edits.
 * A mark with no known reset parks the token for {@link TOKEN_LIMIT_COOLDOWN_MS}; none is
 * honoured further ahead than {@link TOKEN_LIMIT_MAX_MS}.
 *
 * Instants on the wire are `Schema.DateTimeUtc` (ISO-8601 strings encoded). Optional fields
 * are absent, never null.
 *
 * @since 1.0.0
 */
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";
import { Array as Arr, DateTime, Option, Predicate, Schema } from "effect";

// ============================================================================
// Constants
// ============================================================================

/**
 * Hex chars of sha256(token) that name a token durably.
 *
 * @since 1.0.0
 */
export const TOKEN_FINGERPRINT_CHARS = 16;

/**
 * A limit with no known reset parks its token this long.
 *
 * @since 1.0.0
 */
export const TOKEN_LIMIT_COOLDOWN_MS = 60 * 60 * 1000;

/**
 * A mark is honoured at most this far ahead (a weekly limit fits).
 *
 * @since 1.0.0
 */
export const TOKEN_LIMIT_MAX_MS = 8 * 24 * 60 * 60 * 1000;

/** The longest {@link UsageLimit} message kept. */
const MESSAGE_MAX_CHARS = 500;

/** How Claude Code opens a usage-limit message. */
const LIMIT_PREFIX = "You've hit your ";

/** The message when neither the result nor an assistant block says anything. */
const DEFAULT_MESSAGE = "Claude usage limit";

// ============================================================================
// Fingerprint
// ============================================================================

/**
 * A token's durable id: the first {@link TOKEN_FINGERPRINT_CHARS} hex chars of its
 * SHA-256. Safe to log and store; the token is not.
 *
 * @since 1.0.0
 */
export const tokenFingerprint = (token: string): string =>
  bytesToHex(sha256(utf8ToBytes(token))).slice(0, TOKEN_FINGERPRINT_CHARS);

// ============================================================================
// Rpc payloads
// ============================================================================

/**
 * `v3.host.tokens.lease`: a token for one agent of the caller's run.
 *
 * @since 1.0.0
 */
export const TokenLeaseInput = Schema.Struct({ runId: Schema.String, agentId: Schema.String });

/**
 * @since 1.0.0
 */
export type TokenLeaseInput = typeof TokenLeaseInput.Type;

/**
 * A leased token: its index into the pool, its fingerprint, and the raw token (Redacted in
 * memory, a plain string on the wire; never logged).
 *
 * @since 1.0.0
 */
export const TokenLease = Schema.Struct({
  index: Schema.Int.pipe(Schema.nonNegative()),
  fingerprint: Schema.String,
  token: Schema.Redacted(Schema.String),
});

/**
 * @since 1.0.0
 */
export type TokenLease = typeof TokenLease.Type;

/**
 * `v3.host.tokens.exhausted`: an agent of the caller's run hit a usage limit on the token
 * named by `fingerprint`.
 *
 * @since 1.0.0
 */
export const TokenExhaustedInput = Schema.Struct({
  runId: Schema.String,
  agentId: Schema.String,
  fingerprint: Schema.String,
  /** Absent: the reset is unknown. */
  resetsAt: Schema.optional(Schema.DateTimeUtc),
  message: Schema.String,
});

/**
 * @since 1.0.0
 */
export type TokenExhaustedInput = typeof TokenExhaustedInput.Type;

/**
 * Until when the pool parks the token.
 *
 * @since 1.0.0
 */
export const TokenExhaustedResult = Schema.Struct({ exhaustedUntil: Schema.DateTimeUtc });

/**
 * @since 1.0.0
 */
export type TokenExhaustedResult = typeof TokenExhaustedResult.Type;

/**
 * Every token in the pool is at its limit; the earliest frees at `earliestResetAt`.
 *
 * @since 1.0.0
 */
export class AllTokensExhausted extends Schema.TaggedError<AllTokensExhausted>()("AllTokensExhausted", {
  earliestResetAt: Schema.DateTimeUtc,
  message: Schema.String,
}) {}

// ============================================================================
// Usage-limit parser
// ============================================================================

/**
 * A usage-limit exit: Claude Code's words and, when known, when the limit resets.
 *
 * @since 1.0.0
 */
export interface UsageLimit {
  readonly message: string;
  readonly resetsAt: Option.Option<DateTime.Utc>;
}

const RateLimitLine = Schema.Struct({
  type: Schema.Literal("rate_limit_event"),
  rate_limit_info: Schema.Struct({ status: Schema.String, resetsAt: Schema.optional(Schema.Unknown) }),
});

const ResultLine = Schema.Struct({
  type: Schema.Literal("result"),
  is_error: Schema.optionalWith(Schema.Boolean, { default: () => false }),
  api_error_status: Schema.optional(Schema.Unknown),
  result: Schema.optionalWith(Schema.String, { default: () => "" }),
});

const AssistantLine = Schema.Struct({
  type: Schema.Literal("assistant"),
  message: Schema.Struct({
    content: Schema.optionalWith(Schema.Array(Schema.Unknown), { default: () => [] }),
  }),
});

const TextBlock = Schema.Struct({ type: Schema.Literal("text"), text: Schema.String });

const decodeLine = Schema.decodeUnknownOption(Schema.parseJson(Schema.Union(RateLimitLine, ResultLine, AssistantLine)));
const decodeTextBlock = Schema.decodeUnknownOption(TextBlock);

const isLimitText = (text: string): boolean => text.startsWith(LIMIT_PREFIX) || /usage limit/i.test(text);

/**
 * Whether an agent's stream-json transcript ended on a usage limit, and if so its message
 * and reset. Malformed lines are skipped.
 *
 * It is a limit when a `rate_limit_event` is `rejected`, or when the last `result` is an
 * error that is a 429, reads like a limit, or follows an assistant block that opens like
 * one. A successful result never matches on its text: an agent writing about limits must
 * not park a healthy token.
 *
 * @since 1.0.0
 */
export const usageLimitOf = (lines: ReadonlyArray<string>, now: DateTime.Utc): Option.Option<UsageLimit> => {
  const parsed = Arr.filterMap(lines, (line) => decodeLine(line));
  const rejected = Arr.findLast(
    parsed,
    (line) =>
      line.type === "rate_limit_event" && line.rate_limit_info.status === "rejected"
        ? Option.some(line.rate_limit_info)
        : Option.none(),
  );
  const errorResult = Arr.findLast(parsed, (line) => line.type === "result" ? Option.some(line) : Option.none()).pipe(
    Option.filter((result) => result.is_error),
  );
  const limitBlock = Arr.findLast(
    Arr.flatMap(parsed, (line) => line.type === "assistant" ? line.message.content : []),
    (block) =>
      decodeTextBlock(block).pipe(
        Option.map(({ text }) => text),
        Option.filter((text) => text.startsWith(LIMIT_PREFIX)),
      ),
  );
  const errorIsLimit = Option.exists(
    errorResult,
    (result) => result.api_error_status === 429 || isLimitText(result.result) || Option.isSome(limitBlock),
  );
  if (Option.isNone(rejected) && !errorIsLimit) {
    return Option.none();
  }
  const message = errorResult.pipe(
    Option.map((result) => result.result),
    Option.filter((text) => text.length > 0),
    Option.orElse(() => limitBlock),
    Option.getOrElse(() => DEFAULT_MESSAGE),
  ).slice(0, MESSAGE_MAX_CHARS);
  const resetsAt = rejected.pipe(
    Option.flatMap((info) => Option.liftPredicate(info.resetsAt, Predicate.isNumber)),
    Option.flatMap((seconds) => DateTime.make(seconds * 1000)),
    Option.orElse(() => parseResetsAt(message, now)),
  );
  return Option.some({ message, resetsAt });
};

const MONTHS: ReadonlyArray<string> = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * `resets <when> (<zone>)`, where `<when>` is `<Mon> <d>[, <yyyy>](, | at )<h>[:<mm>](am|pm)`
 * or `<h>[:<mm>](am|pm)`. Groups: month, day, year, hour, minute, meridiem, zone.
 */
const RESETS_PATTERN = new RegExp(
  String.raw`resets\s+(?:(${MONTHS.join("|")})\s+(\d{1,2})(?:,\s+(\d{4}))?(?:,\s+|\s+at\s+))?`
    + String.raw`(\d{1,2})(?::(\d{2}))?(am|pm)\s+\(([^)\s]+)\)`,
);

interface LocalParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hours: number;
  readonly minutes: number;
}

const zonedUtc = (parts: LocalParts, timeZone: string): Option.Option<DateTime.Utc> =>
  DateTime.makeZoned(parts, { timeZone, adjustForTimeZone: true }).pipe(Option.map(DateTime.toUtc));

/**
 * When a Claude Code usage-limit message says the limit resets, as an instant: the
 * `resets <when> (<zone>)` phrase anywhere in `text`, read in its zone. With no year, the
 * zone-local year of `now`; with no date, the next such time after `now` (the zone-local
 * date of `now`, else the day after). None when there is no such phrase or the zone is
 * unknown.
 *
 * @since 1.0.0
 */
export const parseResetsAt = (text: string, now: DateTime.Utc): Option.Option<DateTime.Utc> =>
  Option.fromNullable(RESETS_PATTERN.exec(text)).pipe(
    Option.flatMap(([, month, day, year, hour, minute = "0", meridiem, zone]) =>
      DateTime.makeZoned(now, { timeZone: zone }).pipe(
        Option.map(DateTime.toParts),
        Option.flatMap((today) => {
          const hours = (Number(hour) % 12) + (meridiem === "pm" ? 12 : 0);
          const minutes = Number(minute);
          if (month !== undefined) {
            return zonedUtc({
              year: year === undefined ? today.year : Number(year),
              month: MONTHS.indexOf(month) + 1,
              day: Number(day),
              hours,
              minutes,
            }, zone);
          }
          const parts = { year: today.year, month: today.month, day: today.day, hours, minutes };
          return zonedUtc(parts, zone).pipe(
            Option.flatMap((at) =>
              DateTime.greaterThan(at, now) ? Option.some(at) : zonedUtc({ ...parts, day: parts.day + 1 }, zone)
            ),
          );
        }),
      )
    ),
  );
